import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useTabs } from '../hooks/useTabs';

interface TabContextType {
  activeTab: number;
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
  enabledTabs: number[];
  completeTab: (tabIndex: number) => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setEmptyTabs: React.Dispatch<React.SetStateAction<number[]>>;
  numberOfTabs: number;
  emptyTabs: number[];
  resolveEmptyTab: (tabIndex: number) => void;
  setEnabledTabs: (tabs: number[]) => void;
}

const TabContext = createContext<TabContextType | undefined>(undefined);

export const useTabContext = (): TabContextType => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabContext must be used within a TabProvider');
  }
  return context;
};

interface TabProviderProps {
  children: ReactNode;
  numberOfTabs: number;
}

export const TabProvider: React.FC<TabProviderProps> = ({
  children,
  numberOfTabs,
}) => {
  const { activeTab, setActiveTab, enabledTabs, completeTab, setEnabledTabs } = useTabs(numberOfTabs);
  const [loading, setLoading] = useState<boolean>(false);
  const [emptyTabs, setEmptyTabs] = useState<number[]>([]);

  const resolveEmptyTab = (tabIndex: number) => {
    setEmptyTabs([...emptyTabs, tabIndex]);
    completeTab(tabIndex);
  }

  const contextValue = useMemo(
    () => ({
      activeTab,
      setActiveTab,
      enabledTabs,
      completeTab,
      loading,
      setLoading,
      emptyTabs,
      setEmptyTabs,
      resolveEmptyTab,
      numberOfTabs,
      setEnabledTabs
    }),
    [activeTab, enabledTabs, emptyTabs, loading, completeTab],
  );

  return (
    <TabContext.Provider value={contextValue}>{children}</TabContext.Provider>
  );
};
